import { useState } from 'react';
import { PaginatedPatientDto } from 'src/crs/patient/services/PatientService';
import { GridState, PatientList, UsersView } from './PatientList';
import { useSnackbar } from 'notistack';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import API from 'src/ccm/services/api';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { GeneralPractitionerGrid } from 'src/careflow/helpers/types';

const initialGridState: GridState = {
  page: 1,
  rowsPerPage: 5,
  orderBy: 'createdAt',
  orderDirection: 'desc',
  search: '',
  // silFilter: [...initialServiceFilter],
  silFilter: [],
  ownersToFilter: [],
  careTeamMembersToFilter: [],
  includeInactive: false
};

const PatientListContainer = () => {
  const [tableData, setTableData] = useState<PaginatedPatientDto | undefined | null>(null);
  const [search, setSearch] = useState<string>('');
  const [totalItems, setTotalItems] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [ownersToFilter, setOwnersToFilter] = useState<GeneralPractitionerGrid[] | null>(null);
  const [careTeamMembersToFilter, setCareTeamMembersToFilter] = useState<UsersView[] | null>(null);
  const [isDeactivating, setIsDeactivating] = useState<boolean>(false);
  const [isActivating, setIsActivating] = useState<boolean>(false);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

  const { enqueueSnackbar } = useSnackbar();
  const { i18n } = useLocales();

  const refreshPatientList = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleDeactivatePatient = async (patientId: string) => {
    try {
      if (!checkAclValidation({ acls: [crsAcls.CRS.PATIENT.DEACTIVATE] })) {
        enqueueSnackbar(i18n('patients.deactivate.noPermission', 'crs'), { variant: 'error' });
        return;
      }

      setIsDeactivating(true);
      const response = await API.patients.changeStatus(patientId, 'Inactive');

      if (response.success) {
        enqueueSnackbar(i18n('patients.deactivate.success', 'crs'), { variant: 'success' });
        refreshPatientList();
      } else if (response.error === 'VALIDATION_FAILED') {
        const warningMessage = response.message || i18n('patients.deactivate.validationFailed', 'crs');
        enqueueSnackbar(warningMessage, { variant: 'warning' });
      } else {
        enqueueSnackbar(response.message || i18n('patients.deactivate.error', 'crs'), { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar(i18n('patients.deactivate.error', 'crs'), { variant: 'error' });
    } finally {
      setIsDeactivating(false);
    }
  };

  const handleActivatePatient = async (patientId: string) => {
    try {
      if (!checkAclValidation({ acls: [crsAcls.CRS.PATIENT.DEACTIVATE] })) {
        enqueueSnackbar(i18n('patients.activate.noPermission', 'crs'), { variant: 'error' });
        return;
      }

      setIsActivating(true);
      const response = await API.patients.changeStatus(patientId, 'Active');

      if (response.success) {
        enqueueSnackbar(i18n('patients.activate.success', 'crs'), { variant: 'success' });
        refreshPatientList();
      } else {
        enqueueSnackbar(response.message || i18n('patients.activate.error', 'crs'), { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar(i18n('patients.activate.error', 'crs'), { variant: 'error' });
    } finally {
      setIsActivating(false);
    }
  };

  return (
    <PatientList
      includeBreadcrumbs={true}
      tableData={tableData}
      setTableData={setTableData}
      search={search}
      setSearch={setSearch}
      totalItems={totalItems}
      setTotalItems={setTotalItems}
      isLoading={isLoading}
      setIsLoading={setIsLoading}
      initialGridState={initialGridState}
      ownersToFilter={ownersToFilter}
      setOwnersToFilter={setOwnersToFilter}
      careTeamMembersToFilter={careTeamMembersToFilter}
      setCareTeamMembersToFilter={setCareTeamMembersToFilter}
      onDeactivatePatient={handleDeactivatePatient}
      onActivatePatient={handleActivatePatient}
      isDeactivating={isDeactivating}
      isActivating={isActivating}
      refreshTrigger={refreshTrigger}
    />
  );
};

export default PatientListContainer;
